/**
 * @file qty-input.ts
 * @description Шаг и валидация полей количества по единице измерения
 * (довесок phase19.24 этап 0, замечание пользователя 2026-09-20).
 *
 * Зачем файл существует: поля количества по всей админке стояли со
 * `step={0.001}` — стрелка в диалоге «Сколько собрано» уводила план «2 шт» в
 * «0.001», а кладовщик не может собрать тысячную долю пачки. Дробным бывает
 * только развес в килограммах и литрах; штуки, упаковки, граммы и миллилитры
 * считаются целыми.
 *
 * Правило живёт реестром единиц, а не каскадом `if`: новая единица в
 * справочнике каталога — это запись здесь, а не ветка в каждой форме. Ключи —
 * и машинные коды справочника (`pcs`, `g`: так единица приходит в DTO пикеров),
 * и русские сокращения («шт», «г»: так её отдаёт сериализатор сборки).
 *
 * Единицу СЧЁТА строки документа (пачки против содержимого пачки) считаем тем
 * же правилом, что сервер в `picking-serializer.ts` — см. `skuCountUnit`.
 */

import { formatQtyValue } from "./picking-format";

/** Свойства поля ввода количества: шаг стрелок, клавиатура, целочисленность. */
export interface QtyInputProps {
  /** Шаг стрелок `<input type="number">`. */
  step: number;
  /** Клавиатура на мобильном: `numeric` — без точки, `decimal` — с точкой. */
  inputMode: "numeric" | "decimal";
  /** true — дробное значение считается ошибкой ввода. */
  integer: boolean;
}

interface QtyUnitRule extends QtyInputProps {
  /** Сокращение для текста ошибки («Не больше 2 шт»). */
  label: string;
}

/** Целая единица: шаг 1, цифровая клавиатура, дроби запрещены. */
function whole(label: string): QtyUnitRule {
  return { step: 1, inputMode: "numeric", integer: true, label };
}

/** Развесная единица: шаг в граммах/миллилитрах от базовой, дроби разрешены. */
function fractional(label: string): QtyUnitRule {
  return { step: 0.001, inputMode: "decimal", integer: false, label };
}

/**
 * Реестр единиц. Одна запись на единицу, ключи-синонимы ссылаются на неё же:
 * код справочника (`pcs`), сокращение («шт») и полное название («штука») —
 * одно и то же поле в разных DTO.
 */
const PIECE = whole("шт");
const PACK = whole("уп");
const BUNCH = whole("пучок");
const GRAM = whole("г");
const MILLILITRE = whole("мл");
const KILOGRAM = fractional("кг");
const LITRE = fractional("л");
/**
 * Дни — единица не каталога, а сроков в настройках склада («черновик зависает
 * через N дней»). Считаются тем же правилом: целое, шаг 1. Запись в реестре, а
 * не своя проверка в форме: иначе ошибка печаталась бы «Не больше 90 ед.».
 */
const DAY = whole("дн");

const QTY_UNIT_RULES: Record<string, QtyUnitRule> = {
  // Штуки и упаковки — то, что кладовщик берёт с полки.
  pcs: PIECE,
  шт: PIECE,
  штука: PIECE,
  штук: PIECE,
  pack: PACK,
  уп: PACK,
  упак: PACK,
  упаковка: PACK,
  bunch: BUNCH,
  пучок: BUNCH,
  // Граммы и миллилитры целые: «50.001 г» в фасовке — опечатка, не вес.
  g: GRAM,
  г: GRAM,
  гр: GRAM,
  грамм: GRAM,
  ml: MILLILITRE,
  мл: MILLILITRE,
  миллилитр: MILLILITRE,
  // Килограммы и литры дробные: 0.5 кг — нормальный развес.
  kg: KILOGRAM,
  кг: KILOGRAM,
  килограмм: KILOGRAM,
  l: LITRE,
  л: LITRE,
  литр: LITRE,
  // Дни — пороги и сроки в настройках склада.
  day: DAY,
  days: DAY,
  дн: DAY,
  день: DAY,
  дня: DAY,
  дней: DAY,
};

/** Неизвестная единица: считаем поштучно — это безопаснее тысячных долей. */
const QTY_UNIT_FALLBACK = whole("ед");

/** Ключ реестра: регистр, пробелы и точка в «шт.» не должны мешать. */
function normalizeUnit(unit: string | null | undefined): string {
  return (unit ?? "").trim().toLowerCase().replace(/\.+$/, "");
}

function ruleOf(unit: string | null | undefined): QtyUnitRule {
  return QTY_UNIT_RULES[normalizeUnit(unit)] ?? QTY_UNIT_FALLBACK;
}

/**
 * Свойства поля количества по единице: «шт»/«уп»/«г»/«мл» → шаг 1 и целые,
 * «кг»/«л» → шаг 0.001, неизвестная единица → шаг 1 и целые.
 */
export function qtyInputProps(unit: string | null | undefined): QtyInputProps {
  const { step, inputMode, integer } = ruleOf(unit);
  return { step, inputMode, integer };
}

/**
 * Сокращение единицы для подписей и ошибок. Незнакомую единицу возвращаем как
 * есть — лучше показать «пуд», чем съесть смысл; пустую заменяем на «ед».
 */
export function qtyUnitLabel(unit: string | null | undefined): string {
  const key = normalizeUnit(unit);
  return QTY_UNIT_RULES[key]?.label ?? (unit?.trim() || QTY_UNIT_FALLBACK.label);
}

/**
 * «50.000» + «g» → «50 г» — подпись фасовки в строке пикера или карточки (TD-53).
 *
 * Пикеры заказов и склада печатали фасовку прямо из DTO: «50.000 g», «250 ml».
 * Число ведём через `formatQtyValue` (Prisma отдаёт Decimal со всей шкалой),
 * код единицы — через реестр сокращений выше. Фасовки нет (null со старого
 * ответа, нулевое количество) — возвращаем пустую строку: подпись строки
 * склеивается из кусков через «·», и висячее «ед» там лишнее.
 */
export function formatSalePackage(
  saleQuantity: string | number | null | undefined,
  saleUnit: string | null | undefined,
): string {
  if (!saleQuantity || !saleUnit?.trim()) return "";
  return `${formatQtyValue(saleQuantity)} ${qtyUnitLabel(saleUnit)}`;
}

/** «2,5» → 2.5; нечисловое и пустое → null (запятая — обычный ввод с мобильной). */
export function parseQty(value: string | number): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const raw = value.trim().replace(",", ".");
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export interface QtyValidateOptions {
  /** Минимум включительно; по умолчанию 0 — отрицательных количеств не бывает. */
  min?: number;
  /** Максимум включительно (план строки, остаток партии). */
  max?: number;
  /** false — ноль запрещён (недостача, добавление строки в документ). */
  allowZero?: boolean;
}

/**
 * Проверка введённого количества по единице. Возвращает текст ошибки для
 * подписи под полем или null, если значение годное.
 *
 * Текст пишем словами кладовщика и с единицей: «Не больше 2 шт» понятнее, чем
 * «значение превышает максимум».
 */
export function validateQty(
  value: string | number,
  unit: string | null | undefined,
  options: QtyValidateOptions = {},
): string | null {
  const { min = 0, max, allowZero = true } = options;
  const rule = ruleOf(unit);
  const qty = parseQty(value);
  if (qty === null) return "Введите число.";
  if (rule.integer && !Number.isInteger(qty)) return "Введите целое число.";
  if (qty < min) {
    return min === 0
      ? "Количество не может быть отрицательным."
      : `Не меньше ${formatQtyValue(min)} ${rule.label}.`;
  }
  if (!allowZero && qty === 0) return "Количество должно быть больше нуля.";
  if (max !== undefined && qty > max) {
    return `Не больше ${formatQtyValue(max)} ${rule.label}.`;
  }
  return null;
}

/**
 * Единица СЧЁТА строки документа по фасовке — зеркало серверного
 * `formatPickingItemUnit` (`apps/api/src/wms/picking/picking-serializer.ts`):
 *
 * - `saleQuantity != 1` — пачка («Сила жизни 100 г»), считаем штуками;
 * - `saleQuantity == 1` — единица счёта совпадает с единицей содержимого
 *   («1 г» развеса → граммы);
 * - фасовки нет (пустая строка, старый ответ) — штуки.
 *
 * Расхождение с сервером здесь означало бы, что менеджер вводит «0.5», а
 * склад видит в задании «шт» — поэтому правило одно на оба конца.
 */
export function skuCountUnit(
  sku: { saleQuantity?: string | number | null; saleUnit?: string | null } | null,
): string {
  if (!sku) return PIECE.label;
  const qty = sku.saleQuantity === null || sku.saleQuantity === undefined
    ? null
    : parseQty(sku.saleQuantity);
  if (qty === null || qty !== 1) return PIECE.label;
  return sku.saleUnit ? qtyUnitLabel(sku.saleUnit) : PIECE.label;
}
